import React from 'react'

import {
  LoadMoreButton,
  withBreakpoint,
  Tooltip,
  Spinner,
  ErrorMessage,
  Table,
  TableComponents,
} from '@sketch/components'

// Utils
import { capitalize, formatPriceShort } from '@sketch/utils'

// Components
import { handleFetchMore } from 'components/InfiniteList'

// Styles
import {
  LicenseKey,
  CanceledButton,
  Wrapper,
  QuestionMark,
  TooltipWrapper,
  TableWrapper,
} from './RedeemedLicensesList.styles'

// Types
// eslint-disable-next-line no-restricted-imports
import { LicenseRedemptionStatus } from '@sketch/gql-types/expansive'
import {
  GetLicenseRedemptionsQuery,
  GetLicenseRedemptionsQueryVariables,
  useGetLicenseRedemptionsQuery,
  LicenseRedemptionFragment,
} from '@sketch/gql-types'

interface StateContentProps {
  onCancel(license: LicenseRedemptionFragment): void
  state: LicenseRedemptionStatus
}

interface LicenseTableProps {
  licenses: LicenseRedemptionFragment[]
  onCancel: (license: LicenseRedemptionFragment) => void
  isGreaterThanMobile: boolean
}

interface RedeemedLicensesListProps {
  onCancel: (license: LicenseRedemptionFragment) => void
  customerId: string
  pendingOnly?: boolean
}

// Helpers
const REDEMPTIONS_PATH = ['customer', 'licenseRedemptions', 'entries']

const dateIntlFormat = new Intl.DateTimeFormat(['en-US'], {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const dateFormat = (date: Date) => dateIntlFormat.format(date)

const getTooltip = (state: LicenseRedemptionStatus) => {
  switch (state) {
    case 'CANCELED':
      return 'This request was cancelled'
    case 'REDEEMED':
      return 'This license was successfully redeemed and credited to your account'
  }
}

const StateContent: React.FC<StateContentProps> = ({ onCancel, state }) => {
  switch (state) {
    case 'PENDING':
      return (
        <CanceledButton onClick={onCancel} small>
          Cancel
        </CanceledButton>
      )
    case 'CANCELED':
    case 'REDEEMED':
      return (
        <Tooltip content={getTooltip(state)} placement="top">
          {capitalize(state.toLowerCase())}
        </Tooltip>
      )
    default:
      return <>{capitalize(((state as string) || '').toLowerCase())}</>
  }
}

const TABLE_HEADER = [
  { label: 'License key' },
  { label: 'Requested at' },
  { label: 'Email address' },
  { label: 'Credits' },
  { label: 'Status' },
]

// Components
const LicenseTableRaw: React.FC<LicenseTableProps> = ({
  licenses,
  onCancel,
}) => (
  <TableWrapper>
    <Table
      header={TABLE_HEADER}
      items={licenses}
      renderItem={license => (
        <TableComponents.TableRow>
          <TableComponents.TableCell>
            <LicenseKey>{license.serial}</LicenseKey>
          </TableComponents.TableCell>
          <TableComponents.TableCell>
            {dateFormat(new Date(license.createdAt))}
          </TableComponents.TableCell>
          <TableComponents.TableCell>
            {license.ownerEmail}
          </TableComponents.TableCell>
          <TableComponents.TableCell>
            {license.status === 'REDEEMED' ? (
              formatPriceShort(license.credits, { trimDecimalIfZero: true })
            ) : (
              <TooltipWrapper>
                <Tooltip
                  content={
                    <>
                      Any applied credit will be displayed here
                      <br />
                      once you complete the redeem process
                    </>
                  }
                  placement="bottom"
                >
                  <QuestionMark />
                </Tooltip>
              </TooltipWrapper>
            )}
          </TableComponents.TableCell>
          <TableComponents.TableCell>
            <StateContent
              state={license.status}
              onCancel={() => {
                onCancel(license)
              }}
            />
          </TableComponents.TableCell>
        </TableComponents.TableRow>
      )}
    />
  </TableWrapper>
)

export const LicenseTable = withBreakpoint<
  OmitSafe<LicenseTableProps, 'isGreaterThanMobile'>,
  'isGreaterThanMobile'
>(
  'sm',
  'isGreaterThanMobile'
)(LicenseTableRaw)

/*
 * RedeemedLicensesList
 *
 * Renders a list of redeemed licenses
 */
export const RedeemedLicensesList: React.FC<RedeemedLicensesListProps> = ({
  customerId,
  pendingOnly,
  onCancel,
}) => {
  const { data, error, loading, fetchMore } = useGetLicenseRedemptionsQuery({
    variables: {
      customerId,
      status: pendingOnly ? 'PENDING' : null,
    },
    fetchPolicy: 'cache-and-network',
  })

  if (loading && !data) {
    return <Spinner.Flex primary />
  }

  if (error) {
    return <ErrorMessage.Generic icon={null} />
  }

  const licenses = data?.customer.licenseRedemptions.entries

  if (!licenses || !licenses.length) {
    return null
  }

  const { after, totalCount } = data?.customer.licenseRedemptions.meta ?? {
    after: null,
    totalCount: 0,
  }
  const isLoadMoreVisible = !!totalCount && totalCount > licenses.length

  return (
    <Wrapper>
      <LicenseTable licenses={licenses} onCancel={onCancel} />
      {isLoadMoreVisible && (
        <LoadMoreButton
          loading={loading}
          onClick={handleFetchMore<
            GetLicenseRedemptionsQuery,
            GetLicenseRedemptionsQueryVariables
          >(fetchMore, REDEMPTIONS_PATH, {
            after,
          })}
        />
      )}
    </Wrapper>
  )
}
